import axios from 'axios';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'https://sales.arektaboi.com/api';
const TIMEOUT = 10000;

const axiosInstance = axios.create({
  baseURL: API_BASE_URL,
  timeout: TIMEOUT,
  headers: {
    'Content-Type': 'application/json',
  },
  withCredentials: true,
});

let errorHandler = null;
let csrfToken = null;

export const setErrorHandler = (handler) => {
  errorHandler = handler;
};

export const setCSRFToken = (token) => {
  csrfToken = token;
};

function getCSRFToken() {
  return csrfToken;
}

axiosInstance.interceptors.request.use(
  (config) => {
    // Add CSRF token for all non-GET requests
    if (config.method !== 'get' && config.method !== 'GET') {
      const csrfToken = getCSRFToken();
      if (csrfToken) {
        config.headers['X-CSRFToken'] = csrfToken;
      }
    }
    return config;
  },
  (error) => {
    console.error('Request Error:', error);
    return Promise.reject(error);
  }
);

axiosInstance.interceptors.response.use(
  (response) => {
    return response;
  },
  (error) => {
    if (!error.response) {
      console.error('Network Error: No response from server');
      if (errorHandler) {
        errorHandler('Network error! Please check your internet connection.');
      }
      return Promise.reject(error);
    }

    const { status } = error.response;
    console.error(`Response Error: ${status} - ${error.config.url}`);

    if (status === 403 && errorHandler) {
      errorHandler('You do not have permission to perform this action.');
    }

    if (status >= 500 && errorHandler) {
      errorHandler('Server error! Please try again later.');
    }

    if (status === 429 && errorHandler) {
      errorHandler('Too many requests! Please slow down and try again.');
    }

    return Promise.reject(error);
  }
);

class ApiService {
  async request(endpoint, options = {}) {
    try {
      const response = await axiosInstance({
        url: endpoint,
        method: options.method || 'GET',
        data: options.body,
        params: options.params,
        headers: options.headers || {},
      });
      
      return response.data;
    } catch (error) {
      const message = error.response?.data?.error || 
                      error.response?.data?.message || 
                      error.message || 
                      'An unexpected error occurred';
      
      throw new Error(message);
    }
  }

  // Authentication
  async getCurrentUser() {
    return this.request('/orders/auth/me/', {
      method: 'GET',
    });
  }

  async logout() {
    return this.request('/orders/auth/logout/', {
      method: 'POST',
    });
  }

  // OTP
  async validatePromoCode(promoCode) {
    return this.request('/orders/promo-code/', {
      method: 'POST',
      body: { promo_code: promoCode },
    });
  }

  async sendOTP(phoneNumber) {
    return this.request('/orders/otp/', {
      method: 'POST',
      body: { phone_number: phoneNumber },
    });
  }

  async verifyOTP(phoneNumber, otp) {
    return this.request('/orders/otp/verify/', {
      method: 'POST',
      body: { phone_number: phoneNumber, otp },
    });
  }

  // Orders
  async createOrder(orderData) {
    return this.request('/orders/create/', {
      method: 'POST',
      body: orderData,
    });
  }

  async getOrderStatus(orderId) {
    return this.request(`/orders/status/${orderId}/`, {
      method: 'GET',
    });
  }

  async getDeliveryCharge(district) {
    return this.request('/orders/delivery-charge/', {
      method: 'POST',
      body: { district },
    });
  }

  // Search
  async instantSearch(query) {
    return this.request('/search/', {
      method: 'GET',
      params: { q: query },
    });
  }

  // Products
  async getProducts(filters = {}) {
    return this.request('/products/', {
      method: 'GET',
      params: filters,
    });
  }

  async getProductBySlugOrUUID(slugOrUuid) {
    return this.request(`/products/${slugOrUuid}/`, {
      method: 'GET',
    });
  }

  async getCategories(filters = {}) {
    return this.request('/categories/', {
      method: 'GET',
      params: filters,
    });
  }

  async getCategoryBySlugOrId(slugOrId) {
    return this.request(`/categories/${slugOrId}/`, {
      method: 'GET',
    });
  }

  async getWriters(filters = {}) {
    return this.request('/writers/', {
      method: 'GET',
      params: filters,
    });
  }

  async getWriterBySlugOrId(slugOrId) {
    return this.request(`/writers/${slugOrId}/`, {
      method: 'GET',
    });
  }

  async getPublications(filters = {}) {
    return this.request('/publications/', {
      method: 'GET',
      params: filters,
    });
  }

  async getPublicationBySlugOrId(slugOrId) {
    return this.request(`/publications/${slugOrId}/`, {
      method: 'GET',
    });
  }

  // Favourites
  async getFavourites(type = null) {
    const params = type ? { type } : {};
    return this.request('/favourites/', {
      method: 'GET',
      params,
    });
  }

  async toggleFavourite(favouriteType, objectId) {
    return this.request('/favourites/toggle/', {
      method: 'POST',
      body: {
        favourite_type: favouriteType,
        object_id: objectId,
      },
    });
  }

  async addFavourite(favouriteType, objectId) {
    return this.request('/favourites/', {
      method: 'POST',
      body: {
        favourite_type: favouriteType,
        object_id: objectId,
      },
    });
  }

  async removeFavourite(favouriteId) {
    return this.request(`/favourites/${favouriteId}/`, {
      method: 'DELETE',
    });
  }

  // Ads
  async getActiveAds() {
    return this.request('/advertisements/active/', {
      method: 'GET',
    });
  }

  // Analytics
  async getAnalyticsOrders(page = 1, pageSize = 20) {
    return this.request('/orders/analytics/', {
      method: 'GET',
      params: { page, page_size: pageSize },
    });
  }

  // Order History
  async getCustomerOrders(page = 1, pageSize = 20) {
    return this.request('/orders/', {
      method: 'GET',
      params: { page, page_size: pageSize },
    });
  }

  async getOrderDetail(orderId) {
    return this.request(`/orders/${orderId}/`, {
      method: 'GET',
    });
  }

  // Reviews
  async getReviews(page = 1, pageSize = 20) {
    return this.request('/reviews/', {
      method: 'GET',
      params: { page, page_size: pageSize },
    });
  }

  async getBookReviews(productSlugOrUuid) {
    return this.request(`/reviews/book/${productSlugOrUuid}/`, {
      method: 'GET',
    });
  }

  async createReview(productId, content) {
    return this.request('/reviews/', {
      method: 'POST',
      body: { product: productId, content },
    });
  }

  async updateReview(reviewId, content) {
    return this.request(`/reviews/${reviewId}/`, {
      method: 'PATCH',
      body: { content },
    });
  }

  async getMyReviews(page = 1, pageSize = 20) {
    return this.request('/reviews/my_reviews/', {
      method: 'GET',
      params: { page, page_size: pageSize },
    });
  }

  // Comments
  async getBookComments(productSlugOrUuid) {
    return this.request(`/comments/book/${productSlugOrUuid}/`, {
      method: 'GET',
    });
  }

  async createComment(productId, content, replyTo = null) {
    return this.request('/comments/', {
      method: 'POST',
      body: { product: productId, content, reply_to: replyTo },
    });
  }
}

const apiService = new ApiService();

export default apiService;
